import { useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useEntitlement } from "@/hooks/useEntitlement";
import PaywallModal from "@/components/PaywallModal";

/**
 * Hook `usePaywall(service_code)` — wrapper de alto nível para gatear
 * ações que consomem entitlement.
 *
 * Fluxo:
 *   const { authorize, PaywallElement, refresh } = usePaywall("OCR");
 *   const ok = await authorize({ quantity: files.length });
 *   if (!ok) return;                  // usuário cancelou paywall
 *   // ok.reservation_id disponível — chame a API real do serviço
 *   await api.post("/ocr/extract", ...)
 *   await ok.commit();                // idempotente
 *
 * Regras:
 *  - Não envia amount do frontend (Seção 59)
 *  - Se serviço tem franquia suficiente → autoriza automaticamente
 *  - Se tem overage → abre PaywallModal; usuário aceita → debita carteira
 *  - Retorna false se usuário cancelar
 *  - always_paid: sempre abre paywall
 */
export function usePaywall(serviceCode) {
  const { entitlement, refresh, guard } = useEntitlement(serviceCode);
  const [pending, setPending] = useState(null); // { quantity, resolve, guardResult }

  const authorize = useCallback(async ({ quantity = 1 } = {}) => {
    // recarrega para ter o valor mais recente
    await refresh();
    const g = guard(quantity);
    if (g.allowed) {
      // dentro da franquia — apenas reserva
      try {
        const r = await api.post("/billing/usage/reserve", {
          service_code: serviceCode, quantity, use_wallet: false,
        });
        return {
          reservation_id: r.data.reservation_id,
          overage_amount: 0,
          commit: async () => api.post("/billing/usage/commit", {
            service_code: serviceCode, reservation_id: r.data.reservation_id, quantity,
          }).catch(() => {}),
          release: async () => api.post("/billing/usage/release", {
            service_code: serviceCode, reservation_id: r.data.reservation_id, quantity,
          }).catch(() => {}),
        };
      } catch { return false; }
    }
    if (g.needsPaywall) {
      // abre paywall e aguarda escolha do usuário
      return new Promise((resolve) => {
        setPending({ quantity, resolve, guardResult: g });
      });
    }
    return false;
  }, [serviceCode, refresh, guard]);

  const onConfirmPay = useCallback(async (reserveResponse) => {
    // usuário aceitou pagar via carteira (backend já debitou)
    const p = pending;
    setPending(null);
    if (!p) return;
    await refresh();
    p.resolve({
      reservation_id: reserveResponse.reservation_id,
      overage_amount: reserveResponse.overage_amount,
      wallet_charged: reserveResponse.wallet_charged,
      commit: async () => api.post("/billing/usage/commit", {
        service_code: serviceCode,
        reservation_id: reserveResponse.reservation_id,
        quantity: p.quantity,
      }).catch(() => {}),
      release: async () => api.post("/billing/usage/release", {
        service_code: serviceCode,
        reservation_id: reserveResponse.reservation_id,
        quantity: p.quantity,
      }).catch(() => {}),
    });
  }, [pending, serviceCode, refresh]);

  const onCancel = useCallback(() => {
    if (pending) pending.resolve(false);
    setPending(null);
  }, [pending]);

  const PaywallElement = pending ? (
    <PaywallModal
      entitlement={entitlement}
      quantity={pending.quantity}
      overageAmount={pending.guardResult.overageAmount}
      overage_units={pending.guardResult.overage_units || pending.quantity}
      always_paid={pending.guardResult.always_paid}
      onConfirmPay={onConfirmPay}
      onCancel={onCancel}
    />
  ) : null;

  return { authorize, PaywallElement, entitlement, refresh };
}
